import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function PaymentSuccess() {
  const q = useQuery();
  const navigate = useNavigate();
  const sessionId = q.get("session_id");
  const [state, setState] = useState({ phase: "polling", message: "Confirming payment…" });
  const [reportLink, setReportLink] = useState(null);
  const [buyerEmail, setBuyerEmail] = useState(null);
  const [copied, setCopied] = useState(false);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing session_id." });
      return;
    }
    try {
      const s = await api.paymentStatus(sessionId);
      if (s.email) setBuyerEmail(s.email);
      if (s.payment_status !== "paid") {
        setState({ phase: "polling", message: "Confirming payment with Stripe…" });
        return;
      }
      // Paid — check report
      if (s.report_status === "ready" && s.access_token && s.result_id) {
        const link = `${window.location.origin}/report/${s.result_id}?key=${s.access_token}`;
        setReportLink(link);
        setState({ phase: "ready", message: "Your report is ready." });
        return;
      }
      if (s.report_status === "failed") {
        setState({
          phase: "error",
          message: "Report generation failed. Please contact support@thephilosophertest.com — your payment is safe.",
        });
        return;
      }
      setState({ phase: "generating", message: "Writing your report…" });
    } catch (e) {
      setState({ phase: "error", message: "Something went wrong. Please refresh." });
    }
  }, [sessionId]);

  useEffect(() => {
    track("purchase_return");
    poll();
    const t = setInterval(poll, 2500);
    return () => clearInterval(t);
  }, [poll]);

  const copy = async () => {
    if (!reportLink) return;
    try {
      await navigator.clipboard.writeText(reportLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("[PaymentSuccess] clipboard copy failed:", err);
    }
  };

  const goToReport = () => {
    if (reportLink) window.location.assign(reportLink);
  };

  return (
    <div className="relative z-10 mx-auto min-h-[100dvh] w-full max-w-md px-6 py-16" data-testid="payment-success-screen">
      <SEOHead noindex path="/payment/success" title="Preparing your report" />
      <p className="text-[11px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.32em" }}>
        Payment · Confirmation
      </p>
      <h1 className="mt-3 font-serif text-[36px] leading-[1.1]" style={{ color: "var(--gold)" }}>
        {state.phase === "ready" ? "Your report is ready." : "Preparing your report…"}
      </h1>
      <p className="mt-4 font-serif italic text-[16px]" style={{ color: "var(--gold-light)" }} data-testid="payment-status-msg">
        {state.message}
      </p>

      {state.phase !== "error" && (
        <div
          className="mt-6 rounded-lg border px-5 py-4"
          style={{
            borderColor: "rgba(201, 167, 85, 0.28)",
            background: "rgba(22, 24, 33, 0.55)",
          }}
          data-testid="delivery-info-block"
        >
          <p
            className="text-[10px] font-medium uppercase"
            style={{ color: "var(--gold)", letterSpacing: "0.32em" }}
          >
            Delivery
          </p>
          <p
            className="mt-2 text-[14px] leading-relaxed"
            style={{ color: "var(--text)" }}
            data-testid="delivery-info-inbox"
          >
            Your report will arrive in your inbox within 1–3 minutes.
          </p>
          <p
            className="mt-2 text-[13px] leading-relaxed"
            style={{ color: "var(--muted)" }}
            data-testid="delivery-info-spam"
          >
            Check your spam folder and mark the email as &ldquo;not spam&rdquo; — otherwise it may not open properly.
          </p>
        </div>
      )}

      {buyerEmail && state.phase !== "error" && (
        <p
          className="mt-4 text-[13px] leading-relaxed"
          style={{ color: "var(--muted)" }}
          data-testid="email-reassurance-msg"
        >
          Sent to <span style={{ color: "var(--text)" }}>{buyerEmail}</span>.
        </p>
      )}

      {state.phase !== "ready" && state.phase !== "error" && (
        <div className="mt-10 flex justify-center">
          <div className="pulse-core h-14 w-14 rounded-full" style={{
            background: "radial-gradient(circle, var(--gold-light) 0%, var(--gold) 55%, rgba(201,167,85,0.4) 100%)",
          }} />
        </div>
      )}

      {state.phase === "ready" && reportLink && (
        <div className="panel mt-8" data-testid="report-link-panel">
          <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
            Your private access link
          </p>
          <p className="mt-3 break-all rounded border p-3 font-mono text-[12px]"
             style={{ borderColor: "rgba(201,167,85,0.25)", background: "rgba(22,24,33,0.5)", color: "var(--text)" }}
             data-testid="report-link">
            {reportLink}
          </p>
          <p className="mt-3 text-[12px]" style={{ color: "var(--gold-light)" }}>
            ⚠ Save this link — it&apos;s your only access to your report.
          </p>
          <div className="mt-4 flex flex-col gap-3">
            <button className="btn-primary" onClick={goToReport} data-testid="open-report-btn">
              Open my report
            </button>
            <button className="btn-ghost" onClick={copy} data-testid="copy-link-btn">
              {copied ? "Copied ✓" : "Copy link"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-10 text-center">
        <button className="btn-ghost" style={{ width: "auto", padding: "10px 18px" }} onClick={() => navigate("/")}>
          Back to home
        </button>
      </div>
      <Footer />
    </div>
  );
}
